// Extracted from UFPMMemberProfile.vue — shared by Plans panel and Roster profile.

export function planFileBase(memberName: string): string {
  return ('pt-ready-' + (memberName || 'member') + '-training-plan')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64) || 'pt-ready-training-plan'
}

export function planPayload(plan: Record<string, unknown>) {
  return {
    exportedAt: new Date().toISOString(),
    source: 'PT Ready UFPM',
    version: '2.5.2',
    plan,
  }
}

export function planDisplayName(_plan: Record<string, unknown>): string {
  return 'Training Plan'
}

export function getPlanWeeks(plan: Record<string, unknown>): number {
  const weeks = Number(plan.total_weeks || 8)
  return Number.isFinite(weeks) && weeks > 0 ? Math.min(weeks, 52) : 8
}

export function getPlanTemplate(plan: Record<string, unknown>, week: number): Array<Record<string, unknown>> {
  const totalWeeks = getPlanWeeks(plan)
  const phase2Start = Math.ceil(totalWeeks / 2) + 1
  if (week >= phase2Start && Array.isArray(plan.week_template_phase2)) {
    return plan.week_template_phase2 as Array<Record<string, unknown>>
  }
  return Array.isArray(plan.week_template) ? plan.week_template as Array<Record<string, unknown>> : []
}

export function dayName(day: Record<string, unknown>): string {
  const names = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const idx = Number(day.day_of_week || 0)
  return names[idx] || String(day.label || 'Day')
}

export function exerciseTarget(ex: Record<string, unknown>): string {
  const repsDisplay = String(ex.reps_display || '').trim()
  if (repsDisplay) return repsDisplay
  const sets = String(ex.computed_sets || ex.base_sets || '').trim()
  const reps = String(ex.computed_reps || ex.base_reps || ex.reps_unit || '').trim()
  return [sets, reps].filter(Boolean).join(sets && reps ? ' x ' : '')
}

export function planExercises(day: Record<string, unknown>): Array<Record<string, unknown>> {
  const warmup  = Array.isArray(day.warmup)   ? day.warmup   as Array<Record<string, unknown>> : []
  const main    = Array.isArray(day.exercises) ? day.exercises as Array<Record<string, unknown>> : []
  const cooldown = Array.isArray(day.cooldown) ? day.cooldown as Array<Record<string, unknown>> : []
  return [
    ...warmup.map(ex   => ({ ...ex, category: ex.category || 'Warm-up' })),
    ...main.map(ex     => ({ ...ex, category: ex.category || 'Main' })),
    ...cooldown.map(ex => ({ ...ex, category: ex.category || 'Cool-down' })),
  ]
}

// ---- low-level helpers ----

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

function isNativeIos(): boolean {
  const cap = window.Capacitor
  if (!cap || typeof cap.getPlatform !== 'function') return false
  if (typeof cap.isNativePlatform === 'function' && !cap.isNativePlatform()) return false
  return cap.getPlatform() === 'ios'
}

async function shareBlobFile(filename: string, blob: Blob, mimeType: string, title = filename, text?: string): Promise<boolean> {
  const cap = window.Capacitor
  const planShare = cap?.Plugins?.PlanShare as
    | { shareFile?: (options: { filename: string; data: string; mimeType?: string; title?: string }) => Promise<void> }
    | undefined
  if (isNativeIos() && planShare?.shareFile) {
    const bytes = new Uint8Array(await blob.arrayBuffer())
    await planShare.shareFile({ filename, data: uint8ToBase64(bytes), mimeType, title })
    return true
  }
  if (typeof File !== 'function' || !navigator.share) return false
  const file = new File([blob], filename, { type: mimeType.split(';')[0] || mimeType })
  const shareData: ShareData = { files: [file], title }
  if (text) shareData.text = text
  if (navigator.canShare && !navigator.canShare({ files: [file] })) return false
  await navigator.share(shareData)
  return true
}

export async function saveBlob(filename: string, blob: Blob, mimeType: string): Promise<void> {
  const cap = window.Capacitor
  const csvExport = cap?.Plugins?.CsvExport as
    | { save?: (options: { filename: string; data: string; mimeType?: string; base64?: boolean }) => Promise<{ path?: string }> }
    | undefined
  if (
    csvExport?.save
    && typeof cap?.getPlatform === 'function'
    && cap.getPlatform() === 'android'
    && (typeof cap.isNativePlatform !== 'function' || cap.isNativePlatform())
  ) {
    const bytes = new Uint8Array(await blob.arrayBuffer())
    const result = await csvExport.save({ filename, data: uint8ToBase64(bytes), mimeType, base64: true })
    window.showToast?.('Saved to ' + (result?.path || 'Downloads/PT Ready/' + filename))
    return
  }
  if (isNativeIos()) {
    const shared = await shareBlobFile(filename, blob, mimeType)
    if (shared) return
  }
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 1000)
}

function downloadText(filename: string, text: string, type: string): void {
  saveBlob(filename, new Blob([text], { type }), type)
    .catch(() => window.showToast?.('Export failed. Please try again.'))
}

// ---- PDF ----

function pdfText(value: unknown): string {
  return String(value ?? '').replace(/[^\x20-\x7E]/g, ' ').replace(/\s+/g, ' ').trim()
}

function pdfEscape(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)')
}

function wrapLine(text: string, maxChars: number): string[] {
  const words = pdfText(text).split(' ').filter(Boolean)
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (next.length > maxChars && line) { lines.push(line); line = word } else { line = next }
  }
  if (line) lines.push(line)
  return lines.length ? lines : ['']
}

export function buildPlanPdf(plan: Record<string, unknown>, memberName: string): ArrayBuffer {
  const pageWidth = 792, pageHeight = 612, margin = 28, gap = 6
  const columnWidth = (pageWidth - margin * 2 - gap * 6) / 7
  const cardTop = 522, cardBottom = 40, cardHeight = cardTop - cardBottom
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const pages: string[] = []

  const addText = (ops: string[], text: unknown, x: number, y: number, size = 8, bold = false) => {
    ops.push('BT', `/${bold ? 'F2' : 'F1'} ${size} Tf`, `1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm (${pdfEscape(pdfText(text))}) Tj`, 'ET')
  }
  const addRect = (ops: string[], x: number, y: number, w: number, h: number, fill?: string) => {
    if (fill) { ops.push('q', `${fill} rg`, `${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re f`, 'Q') }
    ops.push('q', '0.74 0.76 0.79 RG', `${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re S`, 'Q')
  }

  for (let week = 1; week <= getPlanWeeks(plan); week++) {
    const ops: string[] = []
    addText(ops, planDisplayName(plan), margin, 574, 20, true)
    addText(ops, `Member: ${memberName || 'Member'}    Week ${week} of ${getPlanWeeks(plan)}    Exported: ${new Date().toLocaleDateString()}`, margin, 556, 10)
    addText(ops, 'Generated by PT Ready UFPM Tools', pageWidth - margin - 160, 574, 8)

    const template = getPlanTemplate(plan, week)
    for (let idx = 0; idx < 7; idx++) {
      const day = template.find(entry => Number(entry.day_of_week || 0) === idx + 1)
      const x = margin + idx * (columnWidth + gap)
      addRect(ops, x, cardBottom, columnWidth, cardHeight, '1 1 1')
      addRect(ops, x, cardTop - 24, columnWidth, 24, '0.91 0.84 0.72')
      addText(ops, dayLabels[idx], x + 6, cardTop - 16, 10, true)

      if (!day) { addText(ops, 'No scheduled work', x + 6, cardTop - 42, 8); continue }

      let y = cardTop - 40
      if (day.is_rest) {
        addText(ops, 'Rest / Recovery', x + 6, y, 8, true); y -= 12
        for (const line of wrapLine(String(day.notes || 'Mobility, walking, hydration, and sleep.'), 23).slice(0, 8)) {
          addText(ops, line, x + 6, y, 7); y -= 9
        }
        continue
      }

      for (const line of wrapLine(String(day.focus || day.label || 'Training'), 23).slice(0, 2)) {
        addText(ops, line, x + 6, y, 8, true); y -= 10
      }
      y -= 3

      let clipped = false
      for (const ex of planExercises(day)) {
        const detail = [ex.category, ex.name, exerciseTarget(ex), ex.rest_seconds ? `${ex.rest_seconds}s` : ''].filter(Boolean).join(' | ')
        for (const line of wrapLine(detail, 24)) {
          if (y < cardBottom + 14) { clipped = true; break }
          addText(ops, line, x + 6, y, 6.6); y -= 8
        }
        y -= 2
        if (clipped) break
      }
      if (clipped) addText(ops, '+ more in app', x + 6, cardBottom + 6, 7, true)
    }
    pages.push(ops.join('\n'))
  }

  const objects: string[] = []
  const addObject = (body: string) => { objects.push(body); return objects.length }
  const catalogId    = addObject('<< /Type /Catalog /Pages 2 0 R >>')
  const pagesId      = addObject('')
  const fontRegularId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>')
  const fontBoldId    = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>')
  const pageIds: number[] = []

  for (const content of pages) {
    const stream    = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`
    const contentId = addObject(stream)
    const pageId    = addObject(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontRegularId} 0 R /F2 ${fontBoldId} 0 R >> >> /Contents ${contentId} 0 R >>`)
    pageIds.push(pageId)
  }
  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`

  let pdf = '%PDF-1.4\n'
  const offsets = [0]
  objects.forEach((body, idx) => { offsets.push(pdf.length); pdf += `${idx + 1} 0 obj\n${body}\nendobj\n` })
  const xref = pdf.length
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  for (let i = 1; i < offsets.length; i++) pdf += (offsets[i] ?? 0).toString().padStart(10, '0') + ' 00000 n \n'
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xref}\n%%EOF`
  const encoded = new TextEncoder().encode(pdf)
  return encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength) as ArrayBuffer
}

// ---- public export API ----

export function exportPlanJson(plan: Record<string, unknown>, memberName: string): void {
  downloadText(planFileBase(memberName) + '.json', JSON.stringify(planPayload(plan), null, 2), 'application/json;charset=utf-8')
}

export async function sharePlanJson(plan: Record<string, unknown>, memberName: string): Promise<void> {
  const base = planFileBase(memberName)
  const pdf = new Blob([buildPlanPdf(plan, memberName)], { type: 'application/pdf' })
  const title = 'PT Ready training plan'
  try {
    const sharedFile = await shareBlobFile(base + '.pdf', pdf, 'application/pdf', title, planDisplayName(plan))
    if (sharedFile) return
    const text = JSON.stringify(planPayload(plan))
    if (navigator.share) { await navigator.share({ title, text }); return }
    await navigator.clipboard?.writeText(text)
    window.showToast?.('Plan JSON copied.')
  } catch (_) {
    window.showToast?.('Share canceled.')
  }
}

export function exportPlanPdf(plan: Record<string, unknown>, memberName: string): void {
  const pdf = buildPlanPdf(plan, memberName)
  saveBlob(planFileBase(memberName) + '.pdf', new Blob([pdf], { type: 'application/pdf' }), 'application/pdf')
    .catch(() => window.showToast?.('PDF export failed. Please try again.'))
}

export function exportPlanXlsx(plan: Record<string, unknown>, memberName: string): void {
  const xlsx = (window as unknown as {
    XLSX?: {
      utils: {
        book_new: () => unknown
        aoa_to_sheet: (rows: unknown[][]) => Record<string, unknown>
        book_append_sheet: (wb: unknown, ws: unknown, name: string) => void
      }
      write: (wb: unknown, options: { bookType: 'xlsx'; type: 'array' }) => ArrayBuffer
    }
  }).XLSX
  if (!xlsx?.utils || !xlsx.write) { window.showToast?.('Excel export is unavailable in this build.'); return }

  const wb = xlsx.utils.book_new()
  const summary = [
    ['PT Ready', planDisplayName(plan)],
    ['Member', memberName || ''],
    ['Exported', new Date().toLocaleString()],
    ['Total Weeks', getPlanWeeks(plan)],
    ['Training Days / Week', (Array.isArray(plan.week_template) ? plan.week_template as Array<Record<string, unknown>> : []).filter(day => !(day as Record<string, unknown>).is_rest).length],
  ]
  const summaryWs = xlsx.utils.aoa_to_sheet(summary)
  summaryWs['!cols'] = [{ wch: 22 }, { wch: 46 }]
  xlsx.utils.book_append_sheet(wb, summaryWs, 'Summary')

  const weeklyRows: unknown[][] = [['Week', 'Day', 'Focus', 'Category', 'Exercise', 'Target', 'Rest', 'Notes']]
  for (let week = 1; week <= getPlanWeeks(plan); week++) {
    for (const day of getPlanTemplate(plan, week)) {
      const exercises = planExercises(day)
      if (day.is_rest || !exercises.length) {
        weeklyRows.push([week, dayName(day), day.focus || 'Recovery', 'Rest', 'Rest Day', '', '', day.notes || ''])
      } else {
        for (const ex of exercises) {
          weeklyRows.push([week, dayName(day), day.focus || day.label || '', ex.category || '', ex.name || '', exerciseTarget(ex), ex.rest_seconds ? `${ex.rest_seconds} sec` : '', ex.notes || ''])
        }
      }
    }
  }
  const weeklyWs = xlsx.utils.aoa_to_sheet(weeklyRows)
  weeklyWs['!cols'] = [{ wch: 8 }, { wch: 10 }, { wch: 24 }, { wch: 14 }, { wch: 34 }, { wch: 18 }, { wch: 12 }, { wch: 56 }]
  weeklyWs['!autofilter'] = { ref: 'A1:H' + weeklyRows.length }
  weeklyWs['!freeze'] = { xSplit: 0, ySplit: 1 }
  xlsx.utils.book_append_sheet(wb, weeklyWs, 'Training Plan')

  const data = xlsx.write(wb, { bookType: 'xlsx', type: 'array' })
  saveBlob(planFileBase(memberName) + '.xlsx', new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    .catch(() => window.showToast?.('Excel export failed. Please try again.'))
}
